import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ROUTES } from "@/config/routes";
import { BULK_EXPORT_PREVIEW_LIMIT } from "@/domain/invoices/bulkExportPreview";
import { emptyBulkExportFilters } from "@/domain/invoices/bulkExportFilters";
import type { PeriodShortcut } from "@/domain/invoices/datePeriodShortcuts";
import {
  type InvoiceFilterValues,
  serializeInvoiceFiltersToSearchParams,
  validateInvoiceFilterValues,
} from "@/domain/invoices/invoiceFilterState";
import { BillingGateModal } from "@/features/billing/components/BillingGateModal";
import { usePlanAccess } from "@/features/billing/hooks/usePlanAccess";
import type { GatedCapability } from "@/features/billing/hooks/usePlanAccess";
import type { BillingGateReason } from "@/types/api";
import { BulkExportFiltersPanel } from "@/features/invoices/components/BulkExportFiltersPanel";
import { BulkExportModal } from "@/features/invoices/components/BulkExportModal";
import {
  BulkExportPreviewPanel,
  useBulkExportPreviewItems,
} from "@/features/invoices/components/BulkExportPreviewPanel";
import { useCustomers } from "@/features/invoices/hooks/useCustomers";
import { useInvoiceSearch } from "@/features/invoices/hooks/useInvoiceSearch";
import { PageHeader } from "@/features/layout/components/PageHeader";
import { useFilterSearchButton } from "@/lib/query/useFilterSearchButton";
import { cn } from "@/utils/cn";

function filtersSearchKey(filters: InvoiceFilterValues): string {
  return serializeInvoiceFiltersToSearchParams(
    filters,
    new URLSearchParams(),
  ).toString();
}

export function BulkExportPage() {
  const navigate = useNavigate();
  const { withGate } = usePlanAccess();
  const [billingGate, setBillingGate] = useState<{
    reason: BillingGateReason;
    capability: GatedCapability;
  } | null>(null);
  const [filters, setFilters] = useState<InvoiceFilterValues>(() =>
    emptyBulkExportFilters("last-month"),
  );
  const [activePeriod, setActivePeriod] = useState<PeriodShortcut | null>(
    "last-month",
  );
  const [appliedFilters, setAppliedFilters] = useState<InvoiceFilterValues | null>(
    null,
  );
  const [modalOpen, setModalOpen] = useState(false);

  const customersQuery = useCustomers();
  const customers = customersQuery.data?.items ?? [];
  const filterIssues = validateInvoiceFilterValues(filters);
  const canUseFilters = filterIssues.length === 0;

  const appliedSearchKey = appliedFilters ? filtersSearchKey(appliedFilters) : "";
  const draftSearchKey = filtersSearchKey(filters);
  const filtersStale =
    appliedFilters != null && draftSearchKey !== appliedSearchKey;

  const previewQuery = useInvoiceSearch({
    appliedStatuses: appliedFilters?.statuses ?? [],
    customerIds: appliedFilters?.customerIds ?? [],
    pageSize: BULK_EXPORT_PREVIEW_LIMIT,
    dateFrom: appliedFilters?.dateFrom ?? "",
    dateTo: appliedFilters?.dateTo ?? "",
    amountMin: appliedFilters?.amountMin,
    amountMax: appliedFilters?.amountMax,
    dueDateFrom: appliedFilters?.dueDateFrom,
    dueDateTo: appliedFilters?.dueDateTo,
    enabled: appliedFilters != null,
  });

  const previewItems = useBulkExportPreviewItems(
    previewQuery.data?.items,
    appliedFilters?.statuses ?? [],
  );

  const { searchLoading, markSearchStarted } = useFilterSearchButton(
    appliedSearchKey,
    previewQuery.isLoading,
    previewQuery.isFetching,
    previewQuery.isPlaceholderData,
  );

  const handleSearch = () => {
    if (!canUseFilters) return;
    markSearchStarted(draftSearchKey);
    setAppliedFilters({ ...filters });
  };

  const previewReady = appliedFilters != null;
  const previewLoading = searchLoading;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageHeader
        title="Bulk Export Invoice PDFs"
        lead="Set filters, preview a sample, then start a background ZIP export."
        breadcrumbSuffix="Bulk Export PDFs"
      />

      <div
        className={cn(
          "panel invoices-workspace mb-4 overflow-visible",
          filtersStale && previewReady && "is-pending-search",
        )}
      >
        <div className="workspace-section">
          <BulkExportFiltersPanel
            filters={filters}
            activePeriod={activePeriod}
            customers={customers}
            customersLoading={customersQuery.isLoading}
            customersError={null}
            onChange={(next) => {
              setFilters(next);
              setActivePeriod(null);
            }}
            onPeriodSelect={setActivePeriod}
            onReloadCustomers={() => void customersQuery.refetch()}
          />
          <div className="bulk-export-actions">
            <button
              type="button"
              className="btn-main filter-search-btn shrink-0"
              disabled={!canUseFilters || previewLoading}
              aria-busy={previewLoading || undefined}
              onClick={handleSearch}
            >
              {previewLoading ? "Searching…" : "Search"}
            </button>
            <button
              type="button"
              className="btn-main shrink-0"
              disabled={!canUseFilters}
              onClick={() => {
                const reason = withGate("bulk_export", () => setModalOpen(true));
                if (reason) {
                  setBillingGate({ reason, capability: "bulk_export" });
                }
              }}
            >
              Start export…
            </button>
          </div>
        </div>
      </div>

      {previewReady ? (
        <BulkExportPreviewPanel
          items={previewItems}
          customers={customers}
          filtersStale={filtersStale}
          loading={previewLoading}
          error={previewQuery.error}
        />
      ) : (
        <p className="text-sm text-[var(--muted)]">
          Click <strong className="font-semibold text-[var(--text-secondary)]">Search</strong>{" "}
          to preview up to {BULK_EXPORT_PREVIEW_LIMIT} matching invoices before you export.
        </p>
      )}

      <p className="mt-4 text-sm text-[var(--muted)]">
        <Link to={ROUTES.exports} className="link-quiet">
          Export History
        </Link>
      </p>

      <BulkExportModal
        open={modalOpen}
        filters={filters}
        onClose={() => setModalOpen(false)}
        onStarted={(jobId) => navigate(ROUTES.exportDetail(jobId))}
      />

      <BillingGateModal
        open={billingGate != null}
        reason={billingGate?.reason ?? null}
        capability={billingGate?.capability}
        onClose={() => setBillingGate(null)}
      />
    </div>
  );
}
